import { defineStore } from 'pinia'
import messagesApi from '@/api/messagesApi.js'

export const useConversationsStore = defineStore('conversationsStore', {
    state: () => ({
        items: [],
        loading: false,
        activeConversationId: null,
    }),
    getters: {
        sorted: (s) => [...s.items].sort((a, b) => {
            const da = a.last_message?.created_at || ''
            const db = b.last_message?.created_at || ''
            return String(db).localeCompare(String(da))
        }),
    },
    actions: {
        setActive(id) { this.activeConversationId = id },
        async hydrateConversationMeta(conversationId) {
            // Подтягиваем первую страницу ленты и ищем нужную карточку
            try {
            const list = await messagesApi.getConversations({ limit: 50 })
            const fresh = (list || []).find(x => x?.id === conversationId)
            if (!fresh) return
            const idx = this.items.findIndex(c => c.id === conversationId)
            if (idx === -1) return
            const prev = this.items[idx]
            // Сливаем метаданные, не трогая текущий last_message (он уже актуален)
                    const merged = {
                      ...prev,
                      other_user: fresh.other_user ?? prev.other_user,
                      thread_entity_type: fresh.thread_entity_type ?? prev.thread_entity_type ?? null,
                      thread_entity_id: fresh.thread_entity_id ?? prev.thread_entity_id ?? null,
                      thread_entity_title: fresh.thread_entity_title ?? prev.thread_entity_title ?? null,
                      thread_entity_url: fresh.thread_entity_url ?? prev.thread_entity_url ?? null,
                      // Заголовок: предпочитаем thread_entity_title, затем other_user.name
                      title: fresh.thread_entity_title
                        || fresh.title
                        || fresh.other_user?.name
                        || prev.title
                        || `Диалог #${conversationId}`,
                    }
                    this.items[idx] = merged
                    } catch {
                      /* тихий фейл */
                    }
               },

        async fetchConversations() {
            this.loading = true
            try {
                const list = await messagesApi.getConversations({ limit: 50 })
                this.items = (list || []).map(it => ({
                    ...it,
                    // «липкий» заголовок и ссылка на контекст (если тред контекстный)
                    title: it?.thread_entity_title || it?.title || it?.other_user?.name || `Диалог #${it?.id}`,
                    thread_entity_type: it?.thread_entity_type || null,
                    thread_entity_id: it?.thread_entity_id || null,
                    thread_entity_title: it?.thread_entity_title || null,
                    thread_entity_url: it?.thread_entity_url || null,
                }))
            } finally { this.loading = false }
        },

        async ensure1to1(userId, { forceNew = false } = {}) {
            const { conversation_id } = await messagesApi.ensure1to1(userId, { forceNew })
            return conversation_id
        },

        upsertFromMessageEvent({ conversation_id, message }) {
            const idx = this.items.findIndex(c => c.id === conversation_id)
            if (idx === -1) {
                this.items.unshift({
                    id: conversation_id,
                    other_user: null,
                    last_message: message,
                    unread_count: 0,
                    // если сообщение несёт контекст — используем его как липкий заголовок
                    title: message?.entity_title || `Диалог #${conversation_id}`,
                    thread_entity_type: message?.entity_type || null,
                    thread_entity_id: message?.entity_id || null,
                    thread_entity_title: message?.entity_title || null,
                    thread_entity_url: message?.entity_url || null,
                })
                // Если контекста нет (обычная личка) — дотянем other_user и корректный title
                if (!message?.entity_title) {
                    // Fire-and-forget: фоновое обновление метаданных
                    this.hydrateConversationMeta(conversation_id)
                }
            } else {
                const conv = this.items[idx]
                conv.last_message = message || conv.last_message
                // Обновляем липкий заголовок только при явном контексте в новом сообщении
                if (message?.entity_title) {
                    conv.title = message.entity_title
                    conv.thread_entity_type = message.entity_type || conv.thread_entity_type || null
                    conv.thread_entity_id = message.entity_id || conv.thread_entity_id || null
                    conv.thread_entity_title = message.entity_title
                    conv.thread_entity_url = message.entity_url || conv.thread_entity_url || null
                }
                this.items.splice(idx, 1)
                this.items.unshift(conv)
                if (!message?.entity_title && (!conv.other_user || /^Диалог #/.test(conv.title))) {
                    // Fire-and-forget: фоновое обновление метаданных
                    this.hydrateConversationMeta(conversation_id)
                }
            }
        },

        applyWsEvent(payload) {
            if (!payload?.type) return
            if (payload.type === 'message.created') {
                const cid = payload.conversation_id
                // Дозапросим последнее сообщение, чтобы забрать body + entity_*
                messagesApi.getMessages(cid, { limit: 1 })
                    .then((arr) => {
                        const msg = Array.isArray(arr) && arr.length ? arr[arr.length - 1] : ({ id: payload.message_id, created_at: new Date().toISOString() })
                        this.upsertFromMessageEvent({ conversation_id: cid, message: msg })
                        // Fire-and-forget: фоновое обновление метаданных
                        if (!msg?.entity_title) this.hydrateConversationMeta(cid)
                    })
                    .catch(() => {
                        const msg = { id: payload.message_id, created_at: new Date().toISOString() }
                        this.upsertFromMessageEvent({ conversation_id: cid, message: msg })
                        // Fire-and-forget: фоновое обновление метаданных
                        this.hydrateConversationMeta(cid)
                    })
                    .finally(() => {
                        if (this.activeConversationId !== cid) {
                            const conv = this.items.find(c => c.id === cid)
                            if (conv) conv.unread_count = (conv.unread_count || 0) + 1
                        }
                    })
            } else if (payload.type === 'conversation.read') {
                const conv = this.items.find(c => c.id === payload.conversation_id)
                if (conv && this.activeConversationId === payload.conversation_id) conv.unread_count = 0
            }
        },
    }
})